import { createSlice } from "@reduxjs/toolkit";
import axios from "../../utils/axios";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const initialState = {
  isLoading: false,
  isSaving: false,
  error: null,
  venues: [],
  venue: null,
  page: 1,
  limit: 10,
  totalPages: 0,
  totalResults: 0,
};

function getErrorMessage(error, fallback) {
  if (typeof error === "string") {
    return error;
  }

  return error?.message || error?.error || fallback;
}

function normalizeVenue(venue) {
  if (!venue || typeof venue !== "object") {
    return venue;
  }

  const venueUser = Array.isArray(venue.venueUser)
    ? venue.venueUser.find((user) => user.isSuperAdmin) || venue.venueUser[0]
    : venue.venueUser || venue.user;

  return {
    ...venue,
    phone:
      venue.phone ||
      venue.venuePhoneNumber ||
      venue.ownerPhone ||
      venue.pointOfContactNumber ||
      venueUser?.phone ||
      "",
    email: venue.email || venue.ownerEmail || venueUser?.email || "",
    userName: venue.userName || venueUser?.userName || "",
    dob: venue.dob || venueUser?.dob || "",
    firstName:
      venue.firstName || venue.ownerFirstName || venueUser?.firstName || "",
    lastName:
      venue.lastName || venue.ownerLastName || venueUser?.lastName || "",
    canLogIn: venue.canLogIn ?? venueUser?.canLogIn ?? true,
  };
}

function normalizeListResponse(data, fallbackPage, fallbackLimit) {
  const payload = data?.data || data;
  const resultPayload = payload?.result;
  const results =
    payload?.results ||
    payload?.venues ||
    resultPayload?.results ||
    resultPayload?.venues ||
    resultPayload ||
    [];
  const venues = Array.isArray(results) ? results.map(normalizeVenue) : [];
  const pagination =
    resultPayload && !Array.isArray(resultPayload) ? resultPayload : payload;

  return {
    venues,
    page: pagination?.page || fallbackPage,
    limit: pagination?.limit || fallbackLimit,
    totalPages:
      pagination?.totalPages ||
      Math.ceil((pagination?.totalResults || venues.length) / fallbackLimit),
    totalResults:
      pagination?.totalResults ?? pagination?.total ?? venues.length,
  };
}

function normalizeDetailResponse(data) {
  const venue =
    data?.data?.result ||
    data?.data?.venue ||
    data?.data ||
    data?.result ||
    data?.venue ||
    data;

  return normalizeVenue(venue);
}

const slice = createSlice({
  name: "franchiseVenue",
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
      state.error = null;
    },
    startSaving(state) {
      state.isSaving = true;
      state.error = null;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.isSaving = false;
      state.error = action.payload;
    },
    getVenuesSuccess(state, action) {
      state.isLoading = false;
      state.venues = action.payload.venues;
      state.page = action.payload.page;
      state.limit = action.payload.limit;
      state.totalPages = action.payload.totalPages;
      state.totalResults = action.payload.totalResults;
    },
    getVenueSuccess(state, action) {
      state.isLoading = false;
      state.venue = action.payload;
    },
    saveVenueSuccess(state, action) {
      state.isSaving = false;
      state.venue = action.payload;
    },
    clearVenue(state) {
      state.venue = null;
      state.error = null;
    },
  },
});

export const { clearVenue } = slice.actions;

export default slice.reducer;

export function getFranchiseVenues({ franchiseId, page = 1, limit = 10 }) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.get(
        `${backendUrl}/franchise/${franchiseId}/venues`,
        {
          params: { page, limit },
        }
      );

      dispatch(
        slice.actions.getVenuesSuccess(
          normalizeListResponse(response.data, page, limit)
        )
      );
    } catch (error) {
      dispatch(
        slice.actions.hasError(
          getErrorMessage(error, "Unable to load franchise venues.")
        )
      );
    }
  };
}

export function getAllFranchiseVenues({ franchiseId, limit = 100 }) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      let currentPage = 1;
      let totalPages = 1;
      let totalResults = 0;
      const venueMap = new Map();
      while (currentPage <= totalPages) {
        const response = await axios.get(
          `${backendUrl}/franchise/${franchiseId}/venues`,
          {
            params: {
              page: currentPage,

              limit,
            },
          },
        );

        const normalized = normalizeListResponse(
          response.data,
          currentPage,
          limit,
        );

        normalized.venues.forEach((venue) => {
          const venueId = venue?._id || venue?.id;

          if (venueId) {
            venueMap.set(venueId, venue);
          }
        });

        totalPages = normalized.totalPages || 1;

        totalResults = normalized.totalResults || venueMap.size;

        currentPage += 1;
      }

      const allVenues = Array.from(venueMap.values());

      dispatch(
        slice.actions.getVenuesSuccess({
          venues: allVenues,

          page: 1,

          limit: allVenues.length || limit,

          totalPages: 1,

          totalResults: totalResults || allVenues.length,
        }),
      );

      return allVenues;
    } catch (error) {
      dispatch(
        slice.actions.hasError(
          getErrorMessage(error, "Unable to load franchise venues."),
        ),
      );

      throw error;
    }
  };
}

export function getFranchiseVenue({ franchiseId, venueId }) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.get(
        `${backendUrl}/franchise/${franchiseId}/venues/${venueId}`
      );

      dispatch(
        slice.actions.getVenueSuccess(normalizeDetailResponse(response.data))
      );
      return response.data;
    } catch (error) {
      dispatch(
        slice.actions.hasError(
          getErrorMessage(error, "Unable to load venue details.")
        )
      );
      throw error;
    }
  };
}

export function createFranchiseVenue({ franchiseId, payload }) {
  return async (dispatch) => {
    dispatch(slice.actions.startSaving());

    try {
      const response = await axios.post(
        `${backendUrl}/franchise/${franchiseId}/venues`,
        payload,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      dispatch(
        slice.actions.saveVenueSuccess(normalizeDetailResponse(response.data))
      );
      return response.data;
    } catch (error) {
      dispatch(
        slice.actions.hasError(
          getErrorMessage(error, "Unable to create venue.")
        )
      );
      throw error;
    }
  };
}

export function updateFranchiseVenue({ franchiseId, venueId, payload }) {
  return async (dispatch) => {
    dispatch(slice.actions.startSaving());

    try {
      const response = await axios.patch(
        `${backendUrl}/franchise/${franchiseId}/venues/${venueId}`,
        payload,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      dispatch(
        slice.actions.saveVenueSuccess(normalizeDetailResponse(response.data))
      );
      return response.data;
    } catch (error) {
      dispatch(
        slice.actions.hasError(
          getErrorMessage(error, "Unable to update venue.")
        )
      );
      throw error;
    }
  };
}
